/**
 * client/src/admin/Backlog.tsx — the fourteen failed jobs, and the buttons that act on them.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THERE WERE FOURTEEN FAILED JOBS IN PRODUCTION AND NO SCREEN SHOWED THEM.
 *
 * Twelve `tts` and two `ingest`, on 2026-08-08, while `/api/v1/status` answered 200 and every book on
 * the shelf looked fine. `GET /api/v1/jobs` filters to *your own* jobs on *one* book; `/admin` had
 * four tabs and none of its routes touched the table. The reader's own copy for such a page —
 * *"this page failed and is waiting for a human to retry it"* — was addressed to a human with no
 * button to press. This screen is that button.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **There is no “retry everything” and there must never be one.** `narration/governor.ts` refuses
 * to re-enqueue a failed page automatically and gives the reason: one broken page becomes a permanent
 * GPU loop nobody is told about. So every bulk action here is **priced first** and `apply` defaults to
 * false — the same two-step as `[add to lexicon]` on the TTS tab — and the price names the error most
 * of the batch shares, because twelve failures with one cause are one problem and not twelve buttons.
 *
 * ⚠️ **A drained lane looks exactly like a dead worker.** Queue depth rising, nothing claiming, and no
 * difference visible anywhere else. So the drain banner is the first thing on this screen when it is
 * on, it names who set it and why, and the same flag is reported by `/api/v1/status`.
 *
 * ⚠️ **No new CSS.** Every class here is already in `client/src/ui/app.css`.
 */

import { useCallback, useMemo, useState, type ReactElement } from "react";

import { timeAgo } from "../App.tsx";
import { ApiError, api } from "../lib/api.ts";
import { describeGpu } from "./Generations.tsx";

/* ── the wire ─────────────────────────────────────────────────────────────── */

export interface LaneDrain {
  lane: string;
  drained: boolean;
  reason: string | null;
  setBy: string | null;
  setAt: number | null;
}

export interface BacklogJob {
  id: string;
  kind: string;
  lane: string;
  status: string;
  priority: number;
  attempts: number;
  maxAttempts: number;
  error: string | null;
  message: string | null;
  bookId: string | null;
  bookTitle: string | null;
  actorKey: string | null;
  dedupeKey: string | null;
  workerId: string | null;
  createdAt: number;
  startedAt: number | null;
  finishedAt: number | null;
  runAfter: number | null;
  ageMs: number;
  queuePosition: number | null;
  stalled: boolean;
  goingNowhere: string | null;
}

export interface BacklogResponse {
  jobs: BacklogJob[];
  counts: { lane: string; status: string; n: number }[];
  lanes: LaneDrain[];
  truncated: boolean;
  limit: number;
}

interface BulkRetryPrice {
  applied: boolean;
  jobs: number;
  matching: number;
  limit: number;
  byLane: { lane: string; n: number }[];
  byKind: { kind: string; n: number }[];
  books: number;
  estGpuSec: number;
  oldestAt: number | null;
  dominantError: { error: string; n: number } | null;
  warnings: string[];
  sample: { id: string; kind: string; bookTitle: string | null; attempts: number; error: string | null }[];
  summary: string;
  retried: string[];
}

interface BulkCancelPrice {
  applied: boolean;
  jobs: number;
  matching: number;
  byLane: { lane: string; n: number }[];
  books: number;
  estGpuSecSaved: number;
  running: number;
  summary: string;
  cancelled: string[];
}

const STATUSES = ["failed", "queued", "running", "cancelled", "done"] as const;

/* ── the screen ───────────────────────────────────────────────────────────── */

export function Backlog({
  data,
  error,
  loading,
  lane,
  status,
  onFilter,
  onRefresh,
}: {
  data: BacklogResponse | null;
  error: string | null;
  loading: boolean;
  lane: string;
  status: string;
  onFilter: (next: { lane?: string; status?: string }) => void;
  onRefresh: () => void;
}): ReactElement {
  const counts = useMemo(() => {
    const byStatus = new Map<string, number>();
    for (const c of data?.counts ?? []) byStatus.set(c.status, (byStatus.get(c.status) ?? 0) + c.n);
    return byStatus;
  }, [data]);

  const lanes = data?.lanes ?? [];
  const drained = lanes.filter((l) => l.drained);

  return (
    <>
      {drained.length > 0 ? (
        <section className="ab-panel ab-panel-bad">
          <h2 className="ab-h2">
            {drained.length === 1 ? `The ${drained[0]!.lane} lane is drained` : `${drained.length} lanes are drained`}
          </h2>
          {/* A drained lane and a dead worker are the same picture from every other screen. Saying who
              and when is the difference between "we turned this off on purpose" and a week of blame. */}
          {drained.map((d) => (
            <p key={d.lane} className="ab-hint-bad">
              <strong>{d.lane}</strong> — {d.reason ?? "no reason given"} · set by {d.setBy ?? "the process"}{" "}
              {d.setAt === null ? "" : timeAgo(d.setAt)}. Nothing will claim work on it until you lift it.
            </p>
          ))}
        </section>
      ) : null}

      <section className="ab-panel">
        <h2 className="ab-h2">Lanes</h2>
        <table className="ab-table">
          <thead>
            <tr>
              <th scope="col">Lane</th>
              <th scope="col">Waiting</th>
              <th scope="col">Failed</th>
              <th scope="col">State</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {lanes.map((l) => (
              <LaneRow key={l.lane} drain={l} counts={data?.counts ?? []} onChanged={onRefresh} />
            ))}
          </tbody>
        </table>
      </section>

      <BulkPanel lane={lane} onChanged={onRefresh} />

      <section className="ab-panel">
        <div className="ab-shelf-controls">
          <div className="ab-segment" role="group" aria-label="Which status">
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                className={status === s ? "ab-segment-item is-on" : "ab-segment-item"}
                onClick={() => onFilter({ status: s })}
              >
                {s} ({counts.get(s) ?? 0})
              </button>
            ))}
          </div>
          <div className="ab-select">
            <label className="ab-sr" htmlFor="ab-backlog-lane">
              Which lane
            </label>
            <select id="ab-backlog-lane" value={lane} onChange={(e) => onFilter({ lane: e.target.value })}>
              <option value="">Every lane</option>
              {lanes.map((l) => (
                <option key={l.lane} value={l.lane}>
                  {l.lane}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="ab-btn ab-btn-sm" onClick={onRefresh} disabled={loading}>
            {loading ? "Reading…" : "Refresh"}
          </button>
        </div>

        {error !== null ? <p className="ab-hint-bad">{error}</p> : null}

        {(data?.jobs.length ?? 0) === 0 ? (
          <p className="ab-hint">
            {status === "failed"
              ? "Nothing has failed. That is the answer this screen exists to give, and it is worth checking rather than assuming."
              : "No jobs match."}
          </p>
        ) : (
          <table className="ab-table">
            <thead>
              <tr>
                <th scope="col">Job</th>
                <th scope="col">Book</th>
                <th scope="col">State</th>
                <th scope="col">What went wrong</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {(data?.jobs ?? []).map((j) => (
                <JobRow key={j.id} job={j} onChanged={onRefresh} />
              ))}
            </tbody>
          </table>
        )}

        {data?.truncated === true ? (
          <p className="ab-hint-quiet">
            Showing the newest {data.limit}. Narrow by lane or status to see further back — the counts above
            are over the whole table and are not truncated.
          </p>
        ) : null}
      </section>
    </>
  );
}

/* ── one lane ─────────────────────────────────────────────────────────────── */

function LaneRow({
  drain,
  counts,
  onChanged,
}: {
  drain: LaneDrain;
  counts: { lane: string; status: string; n: number }[];
  onChanged: () => void;
}): ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const n = (status: string): number => counts.find((c) => c.lane === drain.lane && c.status === status)?.n ?? 0;

  const toggle = useCallback(async () => {
    let reason = "";
    if (!drain.drained) {
      const typed = window.prompt(
        `Drain the ${drain.lane} lane? Nothing will claim work on it until you lift it, and it will look ` +
          `exactly like a dead worker on every other screen. Say why:`,
      );
      if (typed === null || typed.trim() === "") return;
      reason = typed.trim();
    }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/admin/lanes/${encodeURIComponent(drain.lane)}/drain`, { drained: !drain.drained, reason });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [drain.lane, drain.drained, onChanged]);

  return (
    <tr>
      <td data-label="Lane">
        <code className="ab-sid">{drain.lane}</code>
      </td>
      <td data-label="Waiting" className="ab-num">
        {n("queued")} queued · {n("running")} running
      </td>
      <td data-label="Failed" className="ab-num">
        {n("failed") === 0 ? <span className="ab-hint-quiet">none</span> : <strong>{n("failed")}</strong>}
      </td>
      <td data-label="State">
        {drain.drained ? (
          <span className="ab-chip ab-chip-bad">drained</span>
        ) : (
          <span className="ab-chip ab-chip-quiet">serving</span>
        )}
        {drain.drained && drain.reason !== null ? <span className="ab-hint-quiet"> {drain.reason}</span> : null}
      </td>
      <td data-label="">
        <button
          type="button"
          className={drain.drained ? "ab-btn ab-btn-primary ab-btn-sm" : "ab-btn ab-btn-danger ab-btn-sm"}
          disabled={busy}
          onClick={() => void toggle()}
        >
          {busy ? "…" : drain.drained ? "Let it serve again" : "Drain"}
        </button>
        {error !== null ? <p className="ab-hint-bad">{error}</p> : null}
      </td>
    </tr>
  );
}

/* ── the bulk operations, priced first ────────────────────────────────────── */

function BulkPanel({ lane, onChanged }: { lane: string; onChanged: () => void }): ReactElement {
  const [limit, setLimit] = useState("25");
  const [price, setPrice] = useState<BulkRetryPrice | null>(null);
  const [cancelPrice, setCancelPrice] = useState<BulkCancelPrice | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const body = useMemo(
    () => ({ ...(lane === "" ? {} : { lane }), limit: Number(limit) }),
    [lane, limit],
  );

  const retry = useCallback(
    async (apply: boolean) => {
      setBusy(true);
      setError(null);
      try {
        const res = await api.post<BulkRetryPrice>("/admin/jobs/retry-failed", { ...body, apply });
        setPrice(res);
        if (apply) onChanged();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [body, onChanged],
  );

  const cancel = useCallback(
    async (apply: boolean) => {
      setBusy(true);
      setError(null);
      try {
        const res = await api.post<BulkCancelPrice>("/admin/jobs/cancel", { ...body, status: "queued", apply });
        setCancelPrice(res);
        if (apply) onChanged();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [body, onChanged],
  );

  return (
    <section className="ab-panel">
      <h2 className="ab-h2">Push the backlog</h2>
      <p className="ab-hint">
        Both of these are <strong>counted before they are spent</strong> and neither runs on a schedule. A
        failure a machine retries forever is a failure nobody reads — that is why there is no
        “retry everything” here, and the reason is written into <code>narration/governor.ts</code>.
      </p>

      <div className="ab-shelf-controls">
        <div className="ab-select">
          <label className="ab-sr" htmlFor="ab-bulk-limit">
            How many at once
          </label>
          <select id="ab-bulk-limit" value={limit} onChange={(e) => setLimit(e.target.value)}>
            <option value="5">5 at a time</option>
            <option value="25">25 at a time</option>
            <option value="50">50 at a time</option>
            <option value="100">100 at a time</option>
          </select>
        </div>
        <span className="ab-hint-quiet">{lane === "" ? "every lane" : `the ${lane} lane`}, oldest failures first</span>
      </div>

      <p className="ab-actions">
        <button type="button" className="ab-btn ab-btn-sm" disabled={busy} onClick={() => void retry(false)}>
          {busy ? "Counting…" : "Count the failures"}
        </button>
        <button type="button" className="ab-btn ab-btn-sm" disabled={busy} onClick={() => void cancel(false)}>
          Count what is queued
        </button>
      </p>

      {error !== null ? <p className="ab-hint-bad">{error}</p> : null}

      {price !== null ? (
        <>
          <p className={price.applied ? "ab-hint" : "ab-hint-bad"}>
            <strong>{price.summary}</strong>
          </p>
          {price.warnings.map((w) => (
            <p key={w} className="ab-hint-bad">
              {w}
            </p>
          ))}
          {price.byLane.length > 0 ? (
            <p className="ab-hint-quiet">
              {price.byLane.map((l) => `${l.n} ${l.lane}`).join(" · ")}
              {price.estGpuSec > 0 ? ` · about ${describeGpu(price.estGpuSec)} of spark-1` : ""}
              {price.oldestAt === null ? "" : ` · oldest ${timeAgo(price.oldestAt)}`}
            </p>
          ) : null}
          {price.sample.length > 0 && !price.applied ? (
            <table className="ab-table">
              <thead>
                <tr>
                  <th scope="col">Job</th>
                  <th scope="col">Book</th>
                  <th scope="col">What went wrong</th>
                </tr>
              </thead>
              <tbody>
                {price.sample.map((s) => (
                  <tr key={s.id}>
                    <td data-label="Job">
                      {s.kind} <span className="ab-hint-quiet">{s.attempts} attempts</span>
                    </td>
                    <td data-label="Book">{s.bookTitle ?? "—"}</td>
                    <td className="ab-table-detail" data-label="What went wrong">
                      {s.error ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {price.applied ? null : (
            <p className="ab-actions">
              <button
                type="button"
                className="ab-btn ab-btn-primary ab-btn-sm"
                disabled={busy || price.jobs === 0}
                onClick={() => void retry(true)}
              >
                {busy ? "Retrying…" : `Retry these ${price.jobs}`}
              </button>
              <button type="button" className="ab-btn ab-btn-sm" onClick={() => setPrice(null)}>
                Cancel
              </button>
            </p>
          )}
        </>
      ) : null}

      {cancelPrice !== null ? (
        <>
          <p className="ab-hint">
            <strong>{cancelPrice.summary}</strong>
          </p>
          {cancelPrice.applied ? null : (
            <p className="ab-actions">
              <button
                type="button"
                className="ab-btn ab-btn-danger ab-btn-sm"
                disabled={busy || cancelPrice.jobs === 0}
                onClick={() => void cancel(true)}
              >
                {busy ? "Cancelling…" : `Cancel these ${cancelPrice.jobs}`}
              </button>
              <button type="button" className="ab-btn ab-btn-sm" onClick={() => setCancelPrice(null)}>
                Leave them
              </button>
            </p>
          )}
        </>
      ) : null}
    </section>
  );
}

/* ── one job ──────────────────────────────────────────────────────────────── */

function JobRow({ job, onChanged }: { job: BacklogJob; onChanged: () => void }): ReactElement {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = useCallback(
    async (what: "retry" | "cancel" | "rush") => {
      setBusy(what);
      setError(null);
      setNote(null);
      try {
        if (what === "retry") {
          const res = await api.post<{ ok: boolean; reason: string | null; warning: string | null }>(
            `/admin/jobs/${encodeURIComponent(job.id)}/retry`,
            {},
          );
          setNote(res.ok ? (res.warning ?? "Queued again from attempt zero.") : (res.reason ?? "Refused."));
        } else if (what === "rush") {
          // 0 is `PRIORITY.regenerate` — in front of everything, because the only reason to press this
          // is that somebody is waiting on it right now.
          const res = await api.post<{ ok: boolean; reason: string | null; from: number | null; to: number }>(
            `/admin/jobs/${encodeURIComponent(job.id)}/priority`,
            { priority: 0 },
          );
          setNote(res.ok ? `Moved from ${res.from} to ${res.to} — it runs next.` : (res.reason ?? "Refused."));
        } else {
          await api.post(`/admin/jobs/cancel`, { ids: [job.id], status: job.status, apply: true });
          setNote("Asked it to stop.");
        }
        onChanged();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(null);
      }
    },
    [job.id, job.status, onChanged],
  );

  const terminal = job.status === "done" || job.status === "failed" || job.status === "cancelled";

  return (
    <tr>
      <td data-label="Job">
        {job.kind}
        <br />
        <span className="ab-hint-quiet">
          <code className="ab-sid">{job.lane}</code> · priority {job.priority} · {timeAgo(job.createdAt)}
        </span>
        {job.dedupeKey === null ? null : (
          <>
            <br />
            <code className="ab-sid">{job.dedupeKey}</code>
          </>
        )}
      </td>

      <td data-label="Book">
        {job.bookTitle ?? (job.bookId === null ? <span className="ab-hint-quiet">—</span> : job.bookId)}
        {job.actorKey === null ? null : (
          <>
            <br />
            <span className="ab-hint-quiet">{job.actorKey}</span>
          </>
        )}
      </td>

      <td data-label="State">
        <span
          className={
            job.status === "failed" ? "ab-chip ab-chip-bad" : job.status === "running" ? "ab-chip ab-chip-work" : "ab-chip"
          }
        >
          {job.status}
        </span>
        <br />
        <span className="ab-hint-quiet">
          {job.attempts} of {job.maxAttempts} attempts
          {job.queuePosition === null ? "" : ` · ${job.queuePosition} ahead`}
          {job.workerId === null ? "" : ` · ${job.workerId}`}
        </span>
        {job.stalled ? (
          <>
            <br />
            {/* `running` with a lapsed lease. The claim will reclaim it; saying so stops somebody
                restarting the worker over a job that is already being recovered. */}
            <span className="ab-chip ab-chip-warn">lease lapsed</span>
          </>
        ) : null}
        {job.goingNowhere === null ? null : (
          <>
            <br />
            <span className="ab-hint-bad">{job.goingNowhere}</span>
          </>
        )}
      </td>

      <td className="ab-table-detail" data-label="What went wrong">
        {job.error ?? job.message ?? <span className="ab-hint-quiet">—</span>}
      </td>

      <td data-label="">
        <span className="ab-actions">
          {job.status === "failed" || job.status === "cancelled" ? (
            <button type="button" className="ab-btn ab-btn-primary ab-btn-sm" disabled={busy !== null} onClick={() => void act("retry")}>
              {busy === "retry" ? "…" : "Retry"}
            </button>
          ) : null}
          {job.status === "queued" && job.priority > 0 ? (
            <button type="button" className="ab-btn ab-btn-sm" disabled={busy !== null} onClick={() => void act("rush")}>
              {busy === "rush" ? "…" : "Run it next"}
            </button>
          ) : null}
          {!terminal ? (
            <button type="button" className="ab-btn ab-btn-danger ab-btn-sm" disabled={busy !== null} onClick={() => void act("cancel")}>
              {busy === "cancel" ? "…" : "Stop"}
            </button>
          ) : null}
        </span>
        {note !== null ? <p className="ab-hint">{note}</p> : null}
        {error !== null ? <p className="ab-hint-bad">{error}</p> : null}
      </td>
    </tr>
  );
}

export default Backlog;
